import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
  FormText,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import httpService from "../../services/httpService";

export default function OrganizationForm(props) {
  const id = props.match.params.id;
  const isNew = id === "new";

  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [emailTemplate, setEmailTemplate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(!isNew);

  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("Agent");

  useEffect(() => {
    if (!isNew) {
      httpService
        .get(`organizations/${id}`)
        .then(({ data }) => {
          setName(data.name || "");
          setDomain(data.domain || "");
          setEmailTemplate(data.emailTemplate || "");
          setIsActive(data.isActive);
          setMembers(data.members || []);
        })
        .catch(() => toast.error("Failed to load organization"))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { name, domain, emailTemplate, isActive };

    const request = isNew
      ? httpService.post("organizations", payload)
      : httpService.put(`organizations/${id}`, payload);

    request
      .then(({ data }) => {
        toast.success(isNew ? "Organization created" : "Organization updated");
        if (isNew && data?.id) {
          props.history.push(`/organizations/${data.id}`);
        }
      })
      .catch((err) => {
        const message =
          err.response?.data?.error || "Failed to save organization";
        toast.error(message);
      });
  };

  const handleDelete = () => {
    if (
      window.confirm("Are you sure you want to delete this organization?")
    ) {
      httpService
        .delete(`organizations/${id}`)
        .then(() => {
          toast.success("Organization deleted");
          props.history.push("/organizations");
        })
        .catch(() => {
          toast.error("Failed to delete organization");
        });
    }
  };

  const handleAddMember = () => {
    if (!newMemberEmail.trim()) return;

    httpService
      .post(`organizations/${id}/members`, {
        email: newMemberEmail,
        role: newMemberRole,
      })
      .then(({ data }) => {
        setMembers([...members, data].sort((a, b) => a.email.localeCompare(b.email)));
        setNewMemberEmail("");
        toast.success("Member added");
      })
      .catch((err) => {
        const message = err.response?.data?.error || "Failed to add member";
        toast.error(message);
      });
  };

  const handleRoleChange = (memberId, newRole) => {
    const prevRole = members.find((m) => m.id === memberId)?.role;
    setMembers(
      members.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
    );
    httpService
      .put(`organizations/${id}/members/${memberId}`, { role: newRole })
      .catch(() => {
        setMembers(
          members.map((m) => (m.id === memberId ? { ...m, role: prevRole } : m))
        );
        toast.error("Failed to update role");
      });
  };

  const handleRemoveMember = (memberId) => {
    if (window.confirm("Remove this member from the organization?")) {
      httpService
        .delete(`organizations/${id}/members/${memberId}`)
        .then(() => {
          setMembers(members.filter((m) => m.id !== memberId));
          toast.success("Member removed");
        })
        .catch(() => {
          toast.error("Failed to remove member");
        });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) return <div className="container mt-3">Loading...</div>;

  return (
    <div className="container mt-3">
      <h2>{isNew ? "Create Organization" : "Edit Organization"}</h2>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="name">Name</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label for="domain">Domain</Label>
          <Input
            id="domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="e.g. company.com"
          />
          <FormText>
            Users with this email domain are automatically treated as
            organization members.
          </FormText>
        </FormGroup>
        <FormGroup>
          <Label for="emailTemplate">Email Template</Label>
          <Input
            id="emailTemplate"
            type="text"
            value={emailTemplate}
            onChange={(e) => setEmailTemplate(e.target.value)}
          />
        </FormGroup>
        <FormGroup check className="mb-3">
          <Label check>
            <Input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />{" "}
            Active
          </Label>
        </FormGroup>
        <div className="d-flex gap-2 mb-4">
          <Button color="primary" type="submit">
            {isNew ? "Create" : "Update"}
          </Button>
          {!isNew && (
            <Button color="danger" type="button" onClick={handleDelete}>
              Delete
            </Button>
          )}
        </div>
      </Form>

      {!isNew && (
        <>
          <hr />
          <h4>Explicit Members</h4>
          {domain && (
            <Alert color="info" className="mb-3">
              Users with <strong>@{domain}</strong> emails are also
              automatically recognized as members.
            </Alert>
          )}

          <div className="d-flex gap-2 mb-3 align-items-end">
            <FormGroup className="mb-0 flex-grow-1">
              <Label for="newMemberEmail" className="small">
                Email
              </Label>
              <Input
                id="newMemberEmail"
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </FormGroup>
            <FormGroup className="mb-0" style={{ minWidth: "120px" }}>
              <Label for="newMemberRole" className="small">
                Role
              </Label>
              <Input
                id="newMemberRole"
                type="select"
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value)}
              >
                <option value="Agent">Agent</option>
                <option value="Manager">Manager</option>
              </Input>
            </FormGroup>
            <Button
              color="primary"
              onClick={handleAddMember}
              style={{ whiteSpace: "nowrap" }}
            >
              Add
            </Button>
          </div>

          {members.length > 0 && (
            <Table striped bordered responsive>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>{m.email}</td>
                    <td>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={m.role}
                        onChange={(e) =>
                          handleRoleChange(m.id, e.target.value)
                        }
                      >
                        <option value="Agent">Agent</option>
                        <option value="Manager">Manager</option>
                      </Input>
                    </td>
                    <td>{formatDate(m.createDate)}</td>
                    <td>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveMember(m.id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {members.length === 0 && (
            <p className="text-muted">No explicit members yet.</p>
          )}
        </>
      )}
    </div>
  );
}
